import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { BEST_BUY_PICKUP_ENABLED } from 'utils/featureFlags';
import type { CartLineAttribute, PolicyCartInput } from './types';
import type { CartPolicyResult } from './policies/types';

/**
 * Best Buy pickup: which lines may share a cart with a pickup ring.
 *
 * A pickup order is one ring, quantity 1 (backend, 28 Oct 2026), plus the
 * add-ons the Ring PRO buy funnel attached to it (MAX, Clue, Luminary CNO
 * Pro: digital, so they ride on the same order). Anything else cannot be
 * clubbed with the Best Buy variant. The client policy
 * (policies/bestBuyPickupExclusive.policy.ts), the server heal (heal.ts) and
 * the checkout verdict (components/ring-pro/BestBuyPickup/pickupCart.ts)
 * all read this one module so they agree.
 *
 * Key strings are duplicated from components/ring-pro/BestBuyPickup/config.ts
 * (BB_LINE_PROPS): lib/ must not import components/. Keep them in step, with
 * validateRequest.ts.
 */
export const BB_PICKUP_LOCATION_KEY = '_bb_pickup_location_id';
const MAX_SUBSCRIPTION = 'MAX_SUBSCRIPTION';

export const BB_PICKUP_BLOCK_REASONS = {
  ONE_RING: 'bb_pickup_one_ring',
  EXCLUSIVE: 'bb_pickup_exclusive',
} as const;

/** Toast copy while the cart UI has no pickup-specific notice for the
 * refusal (policies carry English, as maxQuantity does). */
export const BB_PICKUP_ONE_RING_MESSAGE = 'One ring per Best Buy pickup order.';
export const BB_PICKUP_EXCLUSIVE_MESSAGE =
  "Your cart has a Best Buy pickup ring, which can't be ordered with other items. Switch it to shipping to add this.";

export type PickupLineLike = {
  id: string;
  quantity?: number;
  attributes?: CartLineAttribute[];
};

const getAttr = (
  attributes: CartLineAttribute[] | undefined,
  key: string,
): string | undefined => attributes?.find((a) => a.key === key)?.value;

export const isPickupLine = (
  attributes: CartLineAttribute[] | undefined,
): boolean => getAttr(attributes, BB_PICKUP_LOCATION_KEY) !== undefined;

/** US only, and only where the pickup feature is on. */
export const pickupRuleApplies = (
  store: ShopifyStore | string | null | undefined,
): boolean => store === ShopifyStore.US && BEST_BUY_PICKUP_ENABLED;

export type PickupPartition<L extends PickupLineLike> = {
  /** Pickup ring lines (`_bb_pickup_location_id`). */
  rings: L[];
  /** May sit next to the ring: its own funnel add-ons (same `_bundle_id`)
   * and a MAX subscription with no bundle (cart rail, upsell, /shop). */
  allowed: L[];
  /** Legacy charger lines (`_mirror_parent_qty`): carts built before the
   * pickup order dropped the charger. Checkout removes them at the stamp. */
  chargers: L[];
  /** Everything else: cannot share a cart with the pickup ring. */
  foreign: L[];
};

export function partitionPickupLines<L extends PickupLineLike>(
  lines: L[],
): PickupPartition<L> {
  const rings = lines.filter((line) => isPickupLine(line.attributes));
  const bundles = new Set(
    rings
      .map((ring) => getAttr(ring.attributes, '_bundle_id'))
      .filter((id): id is string => Boolean(id)),
  );
  const allowed: L[] = [];
  const chargers: L[] = [];
  const foreign: L[] = [];
  for (const line of lines) {
    if (isPickupLine(line.attributes)) continue;
    const bundle = getAttr(line.attributes, '_bundle_id');
    const inRingBundle = bundle !== undefined && bundles.has(bundle);
    if (
      getAttr(line.attributes, '_mirror_parent_qty') === '1' &&
      (inRingBundle || bundle === undefined || bundles.size === 0)
    ) {
      chargers.push(line);
    } else if (inRingBundle) {
      allowed.push(line);
    } else if (
      bundle === undefined &&
      getAttr(line.attributes, '_product_type') === MAX_SUBSCRIPTION
    ) {
      allowed.push(line);
    } else {
      foreign.push(line);
    }
  }
  return { rings, allowed, chargers, foreign };
}

const ADD_ID_PREFIX = 'add:';

/**
 * The exclusivity rule on an add request (the client policy's body, pure so
 * it runs in the plain-node harness).
 *
 * - A pickup ring arrives: every foreign line already in the cart leaves in
 *   the same request (remove runs before add in buildCartGraphQL).
 * - A pickup ring arrives while one is in the cart, or two arrive at once:
 *   refused (one ring per pickup order).
 * - The cart holds a pickup ring and a foreign line arrives: refused. The
 *   shopper switches the ring to shipping first.
 */
export function applyPickupExclusivity(
  input: PolicyCartInput,
  cartLines: PickupLineLike[] | null,
  opts: { store: ShopifyStore | string | null; createNewCart?: boolean },
): CartPolicyResult {
  const pass: CartPolicyResult = { input, reasons: [] };
  if (!pickupRuleApplies(opts.store) || opts.createNewCart || !cartLines)
    return pass;
  const adds = input.lineItemsToAdd ?? [];
  if (adds.length === 0) return pass;

  const incoming: PickupLineLike[] = adds.map((line, index) => ({
    id: `${ADD_ID_PREFIX}${index}`,
    quantity: line.quantity,
    attributes: line.attributes,
  }));
  const existingRings = cartLines.filter((line) =>
    isPickupLine(line.attributes),
  );
  const incomingRings = incoming.filter((line) =>
    isPickupLine(line.attributes),
  );
  if (existingRings.length === 0 && incomingRings.length === 0) return pass;

  if (
    incomingRings.length > 1 ||
    (incomingRings.length && existingRings.length)
  ) {
    return {
      input,
      blocked: true,
      blockReason: BB_PICKUP_BLOCK_REASONS.ONE_RING,
      userMessage: BB_PICKUP_ONE_RING_MESSAGE,
      reasons: ['one ring per pickup order'],
    };
  }

  const all = partitionPickupLines([...cartLines, ...incoming]);
  const isIncoming = (line: PickupLineLike) =>
    line.id.startsWith(ADD_ID_PREFIX);

  if (incomingRings.length) {
    const pending = new Set(input.lineItemsToRemove ?? []);
    const remove = all.foreign
      .filter((line) => !isIncoming(line) && !pending.has(line.id))
      .map((line) => line.id);
    if (remove.length === 0) return pass;
    return {
      input: {
        ...input,
        lineItemsToRemove: [...(input.lineItemsToRemove ?? []), ...remove],
      },
      reasons: [`pickup ring arrives: ${remove.length} other line(s) leave`],
    };
  }

  if (all.foreign.some(isIncoming)) {
    return {
      input,
      blocked: true,
      blockReason: BB_PICKUP_BLOCK_REASONS.EXCLUSIVE,
      userMessage: BB_PICKUP_EXCLUSIVE_MESSAGE,
      reasons: ['pickup cart refuses other items'],
    };
  }
  return pass;
}

/**
 * Server backstop (heal.ts): after a request that ADDED lines, foreign lines
 * next to a pickup ring leave. Add-only, so a hydration or attributes-only
 * write (a `?utm_source=` landing, the gift toggle, the hold-expiry clear)
 * never prunes a cart it did not change. Never removes the pickup ring.
 */
export function pickupExclusiveRemovals(
  lines: PickupLineLike[],
  request: { lineItemsToAdd?: unknown[] },
  store: ShopifyStore | string | null,
): string[] {
  if (!pickupRuleApplies(store) || !request.lineItemsToAdd?.length) return [];
  const partition = partitionPickupLines(lines);
  if (partition.rings.length === 0) return [];
  return partition.foreign.map((line) => line.id);
}
